import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { api, authHeaders, getToken, clearToken, API, formatApiError } from "@/lib/api";

const TABS = [
  { key: "donations", label: "Item Donations" },
  { key: "applications", label: "Shopper Applications" },
  { key: "contacts", label: "Contact Messages" },
  { key: "partners", label: "Partner Inquiries" },
  { key: "volunteers", label: "Volunteer Signups" },
  { key: "payments", label: "Monetary Donations" },
];

const STATUSES = ["new", "reviewed", "scheduled", "completed", "declined"];

const LABELS = {
  full_name: "Name", name: "Name", phone: "Phone", email: "Email", preferred_contact: "Contact via",
  city: "City", zip_code: "ZIP", donation_types: "Types", items: "Items", description: "Description",
  quantity: "Qty", condition: "Condition", issues: "Issues", can_drop_off: "Can drop off",
  needs_pickup: "Needs pickup", pickup_address: "Pickup address", stairs: "Stairs", elevator: "Elevator",
  items_location: "Items located", removal_deadline: "Remove by", preferred_pickup_time: "Preferred pickup",
  special_instructions: "Instructions", num_adults: "Adults", num_children: "Children",
  children_ages: "Children's ages", num_bedrooms: "Bedrooms", move_in_date: "Move-in date",
  already_moved_in: "Moved in", transitions: "Transitions", furniture_needs: "Furniture needs",
  essentials_needs: "Essentials needs", can_pick_up: "Can pick up", needs_delivery: "Needs delivery",
  delivery_instructions: "Delivery notes", referred_by_org: "Referred", org_name: "Organization",
  case_manager_name: "Case manager", referral_contact: "Referral contact", subject: "Subject",
  message: "Message", business_name: "Business", location: "Location",
  help_types: "Ways to help", availability: "Availability",
};

const fmt = (v) => (Array.isArray(v) ? v.join(", ") : String(v));

function DetailGrid({ item }) {
  const entries = Object.entries(item).filter(
    ([k, v]) => LABELS[k] && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
  );
  return (
    <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {entries.map(([k, v]) => (
        <p key={k} className="text-sm">
          <span className="font-semibold text-olive">{LABELS[k]}: </span>
          <span className="text-moss">{fmt(v)}</span>
        </p>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("donations");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      const url = which === "payments" ? "/admin/payments" : `/admin/submissions/${which}`;
      const res = await api.get(url, { headers: authHeaders() });
      setItems(res.data.items);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin");
        return;
      }
      toast.error(formatApiError(err, "Could not load submissions."));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    load(tab);
  }, [tab, load, navigate]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${tab}/${id}`, { status }, { headers: authHeaders() });
      setItems(items.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success("Status updated.");
    } catch (err) {
      toast.error(formatApiError(err, "Update failed."));
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="overline-label">Admin Dashboard</p>
          <h1 className="mt-2 font-serif text-3xl text-olive">Submissions &amp; Donations</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-refresh-button" onClick={() => load(tab)} className="btn-outline !px-4 !py-2 text-xs">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button data-testid="admin-logout-button" onClick={logout} className="btn-outline !px-4 !py-2 text-xs">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key ? "border-olive bg-olive text-cream" : "border-linen bg-cream text-moss hover:border-olive/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4" data-testid="admin-submissions-list">
        {loading ? (
          <p className="py-16 text-center text-sage">Loading…</p>
        ) : items.length === 0 ? (
          <p data-testid="admin-empty-state" className="rounded-2xl border border-dashed border-linen py-16 text-center text-sage">
            No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} yet.
          </p>
        ) : tab === "payments" ? (
          items.map((p) => (
            <div key={p.session_id} data-testid={`payment-row-${p.session_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-linen bg-white/60 p-5">
              <div>
                <p className="font-serif text-2xl text-olive">${p.amount}</p>
                <p className="text-xs text-sage">{new Date(p.created_at).toLocaleString()}</p>
              </div>
              <span className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                p.payment_status === "paid" ? "bg-olive text-cream" : "bg-beige text-moss"
              }`}>
                {p.payment_status}
              </span>
            </div>
          ))
        ) : (
          items.map((item) => (
            <div key={item.id} data-testid={`submission-${item.id}`} className="rounded-2xl border border-linen bg-white/60 p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-serif text-xl text-olive">{item.full_name || item.name}</p>
                  <p className="text-xs text-sage">{item.created_at ? new Date(item.created_at).toLocaleString() : ""}</p>
                </div>
                <select
                  data-testid={`status-select-${item.id}`}
                  value={item.status || "new"}
                  onChange={(e) => setStatus(item.id, e.target.value)}
                  className="rounded-full border border-linen bg-cream px-4 py-2 text-xs font-semibold uppercase tracking-wide text-olive"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <DetailGrid item={item} />
              {item.photos && item.photos.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {item.photos.map((p, i) => (
                    <a key={p} href={`${API}/files/${p}?auth=${getToken()}`} target="_blank" rel="noreferrer" data-testid={`photo-link-${item.id}-${i}`}>
                      <img src={`${API}/files/${p}?auth=${getToken()}`} alt={`Donation ${i + 1}`}
                        className="h-20 w-20 rounded-xl border border-linen object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
